import React, { useState } from 'react';
import { Box, Container, Typography, Alert } from '@mui/material';
import { SearchInput } from '../components/ai-search/SearchInput';
import { SearchFilters } from '../components/ai-search/SearchFilters';
import { SearchResults } from '../components/ai-search/SearchResults';
import { searchService } from '../services/search.service';
import { SearchResult, SearchFilters as SearchFiltersType } from '../types/search.types';
import toast from 'react-hot-toast';

export const AISearchPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState<SearchFiltersType>({
    limit: 10,
    threshold: 0.3,  // Changed from 0.7 to match backend
  });

  const handleSearch = async () => {
    if (!query.trim()) return;

    setIsLoading(true);
    setHasSearched(true);
    try {
      const searchResults = await searchService.semanticSearch({
        query,
        limit: filters.limit,
        threshold: filters.threshold,
      });
      setResults(searchResults);
      
      if (searchResults.length === 0) {
        toast('No matching leads found. Try adjusting your search or filters.', {
          icon: '🔍',
        });
      } else {
        toast.success(`Found ${searchResults.length} matching leads`);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error('Search failed. Please try again.');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        AI-Powered Lead Search
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Use natural language to search through your leads using semantic understanding
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Try asking:</strong> tech companies in San Francisco, high priority leads needing follow-up, 
        leads interested in enterprise solutions, or any natural language query
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 300px' }, gap: 3 }}>
        <Box>
          <SearchInput
            value={query}
            onChange={setQuery}
            onSearch={handleSearch}
            isLoading={isLoading}
          />
          
          {hasSearched && (
            <SearchResults results={results} isLoading={isLoading} />
          )}
        </Box>

        <Box>
          <SearchFilters filters={filters} onChange={setFilters} />
        </Box>
      </Box>
    </Container>
  );
};